<template>
    <span class="audio display--flex">
        <span
            class="audio__player--container position--relative accessibility--pseudo accessibility--active"
            tabindex="0"
            :aria-label="$t('accessibility.audioPlayer')"
            role="region"
            @keydown.space="spaceBehavior()"
            @keydown.left="changeTime( -5 )"
            @keydown.right="changeTime( 5 )"
        >
            <span ref="tooltip" class="audio__tooltip position--absolute background--default"></span>
            <span
                ref="player"
                class="audio__player display--flex position--relative background--grey"
                :class="{ 'pointer': hasAudio }"
                @click.self="playFromNewPosition"
                @mousemove="onHover"
                @mouseout="hideHover"
            >
                <span
                    ref="bar"
                    class="audio__bar audio__bar--transition position--absolute background--green"
                    :aria-valuetext="$t('accessibility.audioBar')"
                    aria-valuemin="0"
                    :aria-valuenow="progress"
                    aria-valuemax="100"
                    role="slider"
                ></span>
                <span ref="barHover" class="audio__bar position--absolute background--green--light">
                </span>
                <icon-pause
                    v-if="isPlaying"
                    class="audio__svg pointer"
                    @click.native="pause"
                    :aria-label="$t('accessibility.pause')"
                />
                <icon-loading
                    v-else-if="isFetching"
                    class="audio__svg audio__loading"
                    :aria-label="$t('accessibility.loading')"
                />
                <icon-play
                    v-else
                    class="audio__svg pointer"
                    @click.native="play"
                    :aria-label="$t('accessibility.play')"
                />
                <audio
                    ref="audio"
                    :id="`audioPlayer-${settings.id}`"
                    class="audio__element"
                    @timeupdate="updateCurrentTime"
                    @ended="onEnded"
                    @pause="pause()"
                >
                    <source src="" type="" >
                </audio>
            </span>
            <icon-download
                class="audio__svg__download pointer position--absolute"
                @click.native="exportsFiles( `/contacts/${settings.requestId}/calls/${settings.id}/audio` )"
                @click.native.middle="exportsFiles( `/contacts/${settings.requestId}/calls/${settings.id}/audio` )"
                :aria-label="$t('accessibility.download')"
                :title="$t('global.transfer')"
            />
        </span>
        <span ref="timer" class="audio__time">{{ time }}</span>
        <a class="download" download></a>
    </span>
</template>

<script>
// packages
import { onUnmounted, reactive, toRefs } from '@vue/composition-api';

// Mixins
import exportFiles from '@/helpers/exportFiles';

// configs
import getEndpoint from '@/configs/endpoints';

// helpers
import { get } from '@/helpers/requests';

export default {
    mixins: [ exportFiles ],
    components: {
        IconPause: () => import( /* webpackChunkName: 'IconPause' */ '@/components/bundles/layout/images/icons/IconPause' ),
        IconPlay: () => import( /* webpackChunkName: 'IconPlay' */ '@/components/bundles/layout/images/icons/IconPlay' ),
        IconLoading: () => import( /* webpackChunkName: 'IconLoading' */ '@/components/bundles/layout/images/icons/IconLoading' ),
        IconDownload: () => import( /* webpackChunkName: 'IconDownload' */ '@/components/bundles/layout/images/icons/IconDownload' )
    },
    props: {
        settings: Object
    },
    setup ({ settings }) {
        // definição das variaveis default.
        const state = reactive({
            time: '--:-- / --:--',
            isPlaying: false,
            isFetching: false,
            hasAudio: false
        });

        const elements = reactive({
            player: null,
            bar: null,
            barHover: null,
            tooltip: null,
            audio: null,
            timer: null
        });

        const properties = reactive({
            progress: 0,
            finalWidth: 0,
            newTime: 0
        });

        // Obtenção do audio
        const fetchAudio = async ({ url, model }) => {
            const endpoint = `${getEndpoint[model].crud}/${url}`;
            state.isFetching = true;
            await get( endpoint, {}, { responseType: 'blob' })
                .then( ({ data }) => {
                    const blob = new Blob( [ data ], { type: data.type });
                    const fileUrl = window.URL.createObjectURL( blob ); // gera um url temporário.

                    // Associado url ao player.
                    elements.audio.querySelector( 'source' ).src = fileUrl;
                    elements.audio.querySelector( 'source' ).type = data.type;
                    elements.audio.src = fileUrl;
                    state.isFetching = false;
                    state.hasAudio = true;
                    elements.audio.play();
                }).catch( () => {
                    elements.audio.pause();
                });
        };

        // É activado o audio, que irá executar os seguintes passos.
        //   - Obtém todos players presentes e é dado pause;
        //   - Valida se o player tem `src`, caso tenha é dado play,
        //     caso não tenho, é feito um pedido a API, de forma a obter o audio.
        const play = async () => {
            // obtém todos os players presentes na página e mete-os em pause,
            // de forma a evitar que haja mais que um player a tocar.
            const allPlayers = document.querySelectorAll( `.audio__element:not(#audioPlayer-${settings.id})` );

            allPlayers.forEach( audio => {
                audio.pause();
            });

            // eslint-disable-next-line
            elements.audio.src
                ? elements.audio.play()
                : await fetchAudio({ url: `${settings.requestId}/calls/${settings.id}/audio`, model: settings.model });

            state.isPlaying = true;
        };

        // É pausado o audio.
        const pause = () => {
            state.isPlaying = false;
            // se já tiver pausado, não é necessário
            // estar a executar o mesmo comando.
            if ( elements.audio.paused )
                return;

            elements.audio.pause();
        };

        // Listener para definir o comprimento
        // da barra de hover.
        const onHover = $event => {
            // se não estiver a tocar, não vale a pena
            // estar a escuta.
            if ( !state.hasAudio )
                return;

            // obtenção da posição do player
            // calculo do final comprimento do hover.
            const position = elements.player.getBoundingClientRect();
            properties.finalWidth = parseInt( $event.clientX - position.x, 10 );
            properties.newTime = elements.audio.duration * ( properties.finalWidth / elements.player.offsetWidth );

            const measure = `${properties.finalWidth}px`;

            const { minutes, seconds } = convertTime( properties.newTime );

            elements.barHover.style.width = measure;
            elements.tooltip.classList.add( 'audio__tooltip--active' );
            elements.tooltip.style.left = measure;
            elements.tooltip.innerHTML = `${minutes}:${seconds}`;
        };

        // Listener que volta a definir o comprimento de 0
        // da barra de hover.
        const hideHover = () => {
            elements.barHover.style.width = 0;
            elements.tooltip.classList.remove( 'audio__tooltip--active' );
        };

        // Conversão dos tempos do audio para
        // minutos e segundos.
        const convertTime = time => {
            // documentação do padStart - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/padStart
            const minutes = `${Math.floor( time / 60 )}`.padStart( 2, '0' );
            const seconds = `${Math.floor( time - minutes * 60 )}`.padStart( 2, '0' );

            return { minutes, seconds };
        };

        // update barra & tempo em tempo real.
        let convertDuration = {};

        // Listener para ir actualizando a barra
        // de progresso e o tempo enquanto o audio avança.
        const updateCurrentTime = () => {
            // obtenção dos tempos do audio.
            const { currentTime, duration } = elements.audio;
            // validação se já existe tempo.
            if ( !Number.isNaN( duration ) ) {
                // de forma a evitar que obtenha a duração do audio
                // multiplas vezes, dado que a duração é sempre a mesma,
                // é necessário esta validação.
                if ( !convertDuration.minutes ) {
                    // obtenção da duração do audio em minutos e segundos
                    const { minutes, seconds } = convertTime( duration );
                    convertDuration = { minutes, seconds };
                }

                // obtenção do tempo actual do audio em minutos e segundos.
                const current = convertTime( currentTime );

                // definção dos tempos que vão ser apresentados ao utilizador.
                state.time = `${current.minutes}:${current.seconds} / ${convertDuration.minutes}:${convertDuration.seconds}`;
            }

            properties.progress = ( currentTime / duration ) * 100;

            elements.bar.style.width = `${properties.progress}%`;
        };

        // Listener de quando o audio acaba, é
        // feito o reset da flag e do comprimento
        // da barra de progresso.
        const onEnded = () => {
            state.isPlaying = false;
            elements.bar.style.width = 0;
        };

        const playFromNewPosition = () => {
            elements.audio.currentTime = properties.newTime;
        };

        // navegação com as teclas.
        navigator.mediaSession.setActionHandler( 'play', async () => {
            play();
            navigator.mediaSession.playbackState = 'playing';
            // Do something more than just playing audio...
        });

        navigator.mediaSession.setActionHandler( 'pause', async () => {
            pause();
            navigator.mediaSession.playbackState = 'paused';
            // Do something more than just playing audio...
        });

        // comportamento das setas.
        const changeTime = time => {
            elements.audio.currentTime += time;
        };

        const spaceBehavior = () => {
            ( elements.audio.paused )
                ? play()
                : pause();
        };

        // removido o audio do browser.
        onUnmounted( () => {
            navigator.mediaSession.setActionHandler( 'play', null );
            navigator.mediaSession.setActionHandler( 'pause', null );
        });

        return {
            ...toRefs( state ),
            ...toRefs( elements ),
            ...toRefs( properties ),
            play,
            pause,
            onHover,
            hideHover,
            updateCurrentTime,
            onEnded,
            playFromNewPosition,
            changeTime,
            spaceBehavior
        };
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Animations
    // =============================================================================
    @include keyframes ( minScale ) {
        0% {
            @include transform( scale( .7 ) );
            opacity: 0;
        }
        100% {
            @include transform( scale( 1 ) );
            opacity: 1;
        }
    }

    // =============================================================================
    // Style
    // =============================================================================
    .audio {
        align-items: center;
        width: 500px;
        &__player {
            &--container {
                flex: 1;
                max-width: 370px;
                &:focus {
                    &::before {
                        border: 2px dashed $color-green;
                        @include rounded-corner( 12px );
                    }
                }
            }
            align-items: center;
            height: 35px;
            padding-left: 10px;
            @include rounded-corner( 32px );
            overflow: hidden;
            &::before {
                @include pseudo();
                left: 30px;
                @include dimensions( 80%, 1.5px );
                background: $color-white;
                z-index: 2;
            }
        }
        &__bar {
            left: 0;
            @include dimensions( 0, 100% );
            will-change: width;
            &--transition {
                @include transition( width .3s ease-in-out );
                z-index: 1;
                pointer-events: none;
            }
        }
        &__tooltip {
            top: -20px;
            @include typography-specs( 12px, 12px );
            @include rounded-corner( 4px );
            padding: 5px;
            z-index: 3;
            display: none;
            margin-left: -21px;
            &--active {
                display: block;
            }
        }
        &__svg {
            @include dimensions( 15px, 15px );
            z-index: 2;
            @include animation( minScale .3s ease-in-out );
            &__download {
                @include dimensions( 15px );
                @include transform-position( 50%, 13px, false, false, translateY( 50% ) rotate( 180deg ) );
                transform-origin: top center;
            }
        }
        &__time {
            @include typography-specs( 16px, 21px );
            margin-left: 20px;
        }
    }
</style>